import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PortalType } from '../../../config/enum';
import { usePortal } from '../../../contexts/PortalContext/PortalContext';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import {
  permissionList,
  platformPermissionList,
} from '../../../shared-functions/JWTDocodeFunction';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
} from '../../../shared-functions/LocalStorage';
import { passwordValidation } from '../../../shared-functions/Validation';
import authApi from '../../../utils/api/auth';

/**
 * Login page function component
 */
function NewPasswordChange() {
  const { t, i18n } = useTranslation('loginV2');
  const { portalType, setActivePortal } = usePortal();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const passwordResetDetails = getLocalStorageItem('passwordReset');
  const [credentials, setCredentials] = useState<{
    password: string;
    rePassword: string;
  }>({
    rePassword: '',
    password: '',
  });
  const [submitOpen, setSubmitOpen] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);

  /**
   * Routes to the landing page for the signed-in user's active portal,
   * based on their permissions, falling back to the login page.
   */
  const navigateToPortalHome = () => {
    const permissions = permissionList();
    const platformPermissions = platformPermissionList();

    const storedActivePortal = localStorage.getItem('activePortal');
    let selectedPortal =
      storedActivePortal && platformPermissions.includes(storedActivePortal)
        ? storedActivePortal
        : '';

    if (!selectedPortal) {
      if (portalType && platformPermissions.includes(portalType)) {
        selectedPortal = portalType;
      } else {
        const priorityOrder = [
          PortalType.NETWORKS,
          PortalType.MOBILITY,
          PortalType.CHARGER,
          PortalType.ROAMING,
          PortalType.PARTNER,
          PortalType.USERS,
        ];
        selectedPortal =
          priorityOrder.find((p) => platformPermissions.includes(p)) ||
          platformPermissions[0] ||
          '';
      }
    }

    setActivePortal(selectedPortal);

    const hasPortalAccess = platformPermissions.includes(PortalType.NETWORKS);

    const isSelectedPortal = selectedPortal === PortalType.NETWORKS;
    if (hasPortalAccess && isSelectedPortal) {
      if (permissions.includes('cpoDashboard')) {
        navigate('/ev-admin/dashboard');
      } else if (permissions.includes('cpoPartnerView')) {
        navigate('/ev-admin/accounts/partners');
      } else if (permissions.includes('cpoUtilityProviderView')) {
        navigate('/ev-admin/accounts/utility-provider');
      } else if (permissions.includes('cpoChargingStationView')) {
        navigate('/ev-admin/operations/charging-station/approved');
      } else if (permissions.includes('cpoChargingPointView')) {
        navigate('/ev-admin/operations/chargers');
      } else if (permissions.includes('cpoSessionView')) {
        navigate('/ev-admin/operations/cpo-sessions');
      } else if (permissions.includes('cpoTransactionView')) {
        navigate('/ev-admin/finance/revenue/cpo-transactions');
      } else if (permissions.includes('cpoRevenueSharingView')) {
        navigate('/ev-admin/finance/revenue/revenue-sharing');
      } else if (permissions.includes('cpoCpoPayoutsView')) {
        navigate('/ev-admin/finance/revenue/cpo-payouts/approved');
      } else if (permissions.includes('cpoRevenueView')) {
        navigate('/ev-admin/finance/configuration/revenue-management');
      } else if (permissions.includes('cpoTariffView')) {
        navigate('/ev-admin/tariffs');
      } else if (permissions.includes('cpoEnergyReportView')) {
        navigate('/ev-admin/reports/energy-report');
      } else if (permissions.includes('cpoQrCodeGenerationView')) {
        navigate('/ev-admin/tools/qr-code-generator');
      } else if (permissions.includes('cpoCampaignManagementView')) {
        navigate('/ev-admin/tools/campaign');
      } else if (permissions.includes('cpoTariffCalculation')) {
        navigate('/ev-admin/tools/calculator');
      } else {
        navigate('/login/credentials');
      }
    } else if (
      platformPermissions.includes(PortalType.MOBILITY) &&
      selectedPortal === PortalType.MOBILITY
    ) {
      if (permissions.includes('mobilityEndConsumerView')) {
        navigate('/ev-admin/end-consumer');
      } else if (permissions.includes('mobilityChargersView')) {
        navigate('/ev-admin/operations/personal-charger');
      } else if (permissions.includes('mobilitySessionsView')) {
        navigate('/ev-admin/operations/emsp-sessions');
      } else if (permissions.includes('mobilityGuestUserView')) {
        navigate('/ev-admin/operations/guest-charging');
      } else if (permissions.includes('mobilityTransactionsView')) {
        navigate('/ev-admin/finance/revenue/emsp-transactions');
      } else if (permissions.includes('mobilityPayoutsView')) {
        navigate('/ev-admin/finance/revenue/emsp-payouts/approved');
      } else if (permissions.includes('mobilityFeeConfigurationView')) {
        navigate('/ev-admin/configuration/fee-configuration');
      } else if (permissions.includes('mobilityPaymentGatewayView')) {
        navigate('/ev-admin/configuration/paymentGateway');
      } else {
        navigate('/login/credentials');
      }
    } else if (
      platformPermissions.includes(PortalType.CHARGER) &&
      selectedPortal === PortalType.CHARGER
    ) {
      if (permissions.includes('devicesDashboard')) {
        navigate('/ev-admin/ev-device-hub/dashboard');
      } else if (permissions.includes('devicesEvDeviceHubView')) {
        navigate('/ev-admin/ev-device-hub/devices');
      } else if (permissions.includes('devicesRatingView')) {
        navigate('/ev-admin/connector');
      } else if (permissions.includes('devicesBrandsView')) {
        navigate('/ev-admin/brand');
      } else if (permissions.includes('devicesModelsView')) {
        navigate('/ev-admin/model');
      } else {
        navigate('/login/credentials');
      }
    } else if (
      platformPermissions.includes(PortalType.ROAMING) &&
      selectedPortal === PortalType.ROAMING
    ) {
      navigate('/ev-admin/ocpi');
    } else if (
      platformPermissions.includes(PortalType.PARTNER) &&
      selectedPortal === PortalType.PARTNER
    ) {
      navigate('/ev-admin/partner');
    } else if (
      platformPermissions.includes(PortalType.USERS) &&
      selectedPortal === PortalType.USERS
    ) {
      if (permissions.includes('userManagementView')) {
        navigate('/ev-admin/user-management/user');
      } else if (permissions.includes('userRoleManagementView')) {
        navigate('/ev-admin/user-management/role');
      } else {
        navigate('/ev-admin/profile');
      }
    } else {
      navigate('/login/credentials');
    }
  };

  /**
   * Input field change function
   * @param {any} name - The name
   * @param {any} changeValue - The changeValue
   */
  const handleCredentialChange = (name: string, changeValue: string | number) => {
    setCredentials({
      ...credentials,
      [name]: changeValue,
    });
  };

  /**
   * Whether the new-password field should show an error state.
   * @returns {boolean} true when field is empty or fails the password rule
   */
  const hasPasswordError = () => submitOpen && !passwordValidation(credentials.password);

  /**
   * Resolves which message to show under the new-password field: required
   * first, otherwise the format rule.
   * @returns {string} translated error message
   */
  const passwordErrorMessage = () =>
    credentials.password ? t('passwordValidationHint') : t('pleaseEnterPassword');

  /**
   * Whether the confirm-password field should show an error state.
   * @returns {boolean} true when field is empty or doesn't match the password
   */
  const hasConfirmPasswordError = () =>
    submitOpen && credentials.rePassword !== credentials.password;

  /**
   * Resolves which message to show under the confirm-password field:
   * required first, otherwise the mismatch message.
   * @returns {string} translated error message
   */
  const confirmPasswordErrorMessage = () =>
    credentials.rePassword ? t('passwordAndConfirmMustMatch') : t('confirmPasswordRequired');

  /**
   * handle submit function
   */
  const submitFunction = () => {
    setSubmitOpen(true);
    if (
      PortalType.PARTNER !== portalType &&
      passwordValidation(credentials.password) &&
      credentials.password === credentials.rePassword
    ) {
      setDisabled(true);
      const request = {
        countryCode: passwordResetDetails?.countryCode,
        emailOrPhoneNumber: passwordResetDetails?.emailOrPhoneNumber,
        otp: passwordResetDetails?.otp,
        newPassword: credentials.password,
      };
      authApi
        .createPassword(request)
        .then(() => {
          removeLocalStorageItem('passwordReset');
          setDisabled(false);
          setSuccessModalOpen(true);
        })
        .catch((error) => {
          setDisabled(false);
          if (!error.response) {
            dispatch(
              setNotificationMessage({
                message: 'No internet connection. Please check your network and try again.',
                status: true,
                type: 'error',
              }),
            );
            return;
          }
          dispatch(
            setNotificationMessage({
              message: error?.response?.data?.message,
              status: true,
              type: 'error',
              code: error?.response?.data?.code,
            }),
          );
        });
    }
    if (
      PortalType.PARTNER === portalType &&
      passwordValidation(credentials.password) &&
      credentials.password === credentials.rePassword
    ) {
      setDisabled(true);

      const requestBody = {
        countryCode: passwordResetDetails?.countryCode,
        accountCustomId: passwordResetDetails?.accountCustomId
          ? passwordResetDetails.accountCustomId
          : undefined,
        emailOrPhoneNumber: passwordResetDetails?.emailOrPhoneNumber,
        otp: passwordResetDetails?.otp,
        newPassword: credentials.password,
      };

      authApi
        .createPassword(requestBody)
        .then(() => {
          removeLocalStorageItem('passwordReset');
          setDisabled(false);
          setSuccessModalOpen(true);
        })
        .catch((error) => {
          setDisabled(false);
          if (!error.response) {
            dispatch(
              setNotificationMessage({
                message: 'No internet connection. Please check your network and try again.',
                status: true,
                type: 'error',
              }),
            );
            return;
          }
          // Display a user-friendly message
          dispatch(
            setNotificationMessage({
              type: 'error',
              status: true,
              message: error?.response?.data?.message || t('somethingWentWrong'),
              code: error?.response?.data?.code,
            }),
          );
        });
    }
  };

  /**
   * Closes the success modal and routes the user onward: partner accounts
   * go back to the login page, everyone else lands on their portal home.
   */
  const handleSuccessModalClose = () => {
    setSuccessModalOpen(false);
    if (PortalType.PARTNER === portalType) {
      navigate('../login/credentials');
    } else {
      navigateToPortalHome();
    }
  };

  return (
    <div className="login-v2-wrapper" dir={i18n.dir()}>
      <ModalBox
        open={successModalOpen}
        title={null}
        content={
          <SuccessNotification
            title={t('passwordChangedSuccessfully')}
            description={t('yourPasswordHasBeenChanged')}
            notificationCloseTimer={3000}
            onNotificationClose={handleSuccessModalClose}
          />
        }
        closeModal={handleSuccessModalClose}
        notification
        header={false}
      />
      <CustomInput
        inputLabel={t('createNewPassword')}
        type="password"
        placeholder={t('enterPassword')}
        name="password"
        value={credentials.password}
        onChangeValue={handleCredentialChange}
        error={hasPasswordError()}
        errorMessage={passwordErrorMessage()}
      />
      <CustomInput
        inputLabel={t('confirmPassword')}
        type="password"
        placeholder={t('enterPassword')}
        name="rePassword"
        value={credentials.rePassword}
        onChangeValue={handleCredentialChange}
        error={hasConfirmPasswordError()}
        errorMessage={confirmPasswordErrorMessage()}
      />
      <MyButton
        label={t('submit')}
        buttonType="submit"
        onClickFunc={submitFunction}
        loading={disabled}
        disableButton={disabled}
      />
    </div>
  );
}

export default NewPasswordChange;
